import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    Wrench,
    Play,
    Pause,
    RefreshCw,
    CheckCircle,
    XCircle,
    Clock,
    Zap,
    Shield,
    Server,
    User,
    Globe,
    Lock,
    Trash2,
    AlertTriangle,
    History,
    Settings,
    Loader2,
    ChevronRight,
    Terminal,
    Database,
    HardDrive
} from 'lucide-react';
import { toast } from 'sonner';
import { FeatureGate } from '../components/common/FeatureGate';
import {
    getAvailableFixes,
    getAutofixHistory,
    getAutofixRecommend,
    executeFix as executeFixApi
} from '../lib/api';

const AutoFixContent = () => {
    const [availableFixes, setAvailableFixes] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [executing, setExecuting] = useState(false);
    const [selectedFix, setSelectedFix] = useState(null);
    const [showExecuteDialog, setShowExecuteDialog] = useState(false);
    const [executeForm, setExecuteForm] = useState({ target: '', parameters: {} });
    const [recommendations, setRecommendations] = useState(null);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);
    const [activeTab, setActiveTab] = useState('available');

    const fetchAvailableFixesAsync = async () => {
        try {
            const response = await getAvailableFixes();
            const data = response.data;
            setAvailableFixes(data?.fixes ?? (Array.isArray(data) ? data : []));
        } catch (error) {
            console.error('Failed to fetch fixes:', error);
        }
    };

    const fetchHistoryAsync = async () => {
        try {
            const response = await getAutofixHistory();
            setHistory(response.data?.history || []);
        } catch (error) {
            console.error('Failed to fetch history:', error);
        }
    };

    const fetchRecommendations = async () => {
        setLoadingRecommendations(true);
        try {
            const response = await getAutofixRecommend({ type: 'general_scan' });
            setRecommendations(response.data);
        } catch (error) {
            console.error('Failed to fetch recommendations:', error);
        }
        setLoadingRecommendations(false);
    };

    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([fetchAvailableFixesAsync(), fetchHistoryAsync()]);
        setLoading(false);
    };

    const handleExecuteFix = async () => {
        if (!selectedFix || !executeForm.target) return;
        
        setExecuting(true);
        try {
            const response = await executeFixApi({
                action: selectedFix.id,
                target: executeForm.target,
                parameters: executeForm.parameters
            });
            const result = response.data;
            if (result?.success) {
                toast.success(`Fix executed successfully: ${result.message}`);
            } else {
                toast.error(`Fix failed: ${result?.error || result?.message || 'Unknown error'}`);
            }
            setShowExecuteDialog(false);
            setSelectedFix(null);
            setExecuteForm({ target: '', parameters: {} });
            await fetchHistoryAsync();
        } catch (error) {
            toast.error('Failed to execute fix');
        }
        setExecuting(false);
    };

    const handleQuickFix = async (action, target) => {
        setExecuting(true);
        try {
            const response = await executeFixApi({ action, target, parameters: {} });
            const result = response.data;
            if (result?.success) {
                toast.success(`Fix executed: ${result.message}`);
            } else {
                toast.error(`Fix failed: ${result?.error || result?.message || 'Unknown error'}`);
            }
            await fetchHistoryAsync();
        } catch (error) {
            toast.error('Failed to execute fix');
        }
        setExecuting(false);
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    const getRiskBadge = (riskLevel) => {
        const styles = {
            low: 'bg-green-100 text-green-800 border-green-300',
            medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            high: 'bg-red-100 text-red-800 border-red-300'
        };
        return <Badge variant="outline" className={styles[riskLevel]}>{riskLevel}</Badge>;
    };

    const getStatusBadge = (success) => {
        return success ? (
            <Badge className="bg-green-500 text-white">Success</Badge>
        ) : (
            <Badge className="bg-red-500 text-white">Failed</Badge>
        );
    };

    const getFixIcon = (fixId) => {
        const icons = {
            patch_software: <HardDrive className="h-5 w-5 text-blue-500" />,
            kill_process: <Terminal className="h-5 w-5 text-red-500" />,
            disable_account: <User className="h-5 w-5 text-orange-500" />,
            force_password_reset: <Lock className="h-5 w-5 text-yellow-500" />,
            enable_mfa: <Shield className="h-5 w-5 text-green-500" />,
            block_ip: <Globe className="h-5 w-5 text-red-500" />,
            isolate_device: <Server className="h-5 w-5 text-purple-500" />,
            revoke_sessions: <Trash2 className="h-5 w-5 text-orange-500" />,
            scan_malware: <Shield className="h-5 w-5 text-blue-500" />,
            backup_data: <Database className="h-5 w-5 text-green-500" />,
            clear_cache: <Trash2 className="h-5 w-5 text-gray-500" />,
            restart_service: <RefreshCw className="h-5 w-5 text-blue-500" />
        };
        return icons[fixId] || <Wrench className="h-5 w-5 text-gray-500" />;
    };

    const getTargetPlaceholder = (fixId) => {
        const placeholders = {
            patch_software: 'Device name (e.g., Workstation-01)',
            kill_process: 'Device name',
            disable_account: 'Email address',
            force_password_reset: 'Email address',
            enable_mfa: 'Email address',
            block_ip: 'IP address (e.g., 192.168.1.100)',
            isolate_device: 'Device name',
            revoke_sessions: 'Email address',
            scan_malware: 'Device name',
            backup_data: 'Device name',
            clear_cache: 'Device name',
            restart_service: 'Device name'
        };
        return placeholders[fixId] || 'Target';
    };

    if (loading) {
        return (
            <div className="p-6">
                <Card>
                    <CardContent className="flex items-center justify-center py-12">
                        <Wrench className="h-8 w-8 animate-pulse text-primary mr-3" />
                        <span>Loading auto-fix capabilities...</span>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6" data-testid="autofix-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                        <Wrench className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Auto-Fix & Remediation</h1>
                        <p className="text-sm text-muted-foreground">Automated security fixes and system remediation</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchAllData} data-testid="refresh-autofix-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                    <Button onClick={fetchRecommendations} disabled={loadingRecommendations} data-testid="get-recommendations-btn">
                        <Zap className={`h-4 w-4 mr-2 ${loadingRecommendations ? 'animate-spin' : ''}`} />
                        {loadingRecommendations ? 'Analyzing...' : 'Get AI Recommendations'}
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Available Fixes</p>
                                <p className="text-3xl font-bold">{availableFixes.length}</p>
                            </div>
                            <Wrench className="h-10 w-10 text-blue-500 opacity-20" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Successful Fixes</p>
                                <p className="text-3xl font-bold text-green-500">
                                    {history.filter(h => h.success).length}
                                </p>
                            </div>
                            <CheckCircle className="h-10 w-10 text-green-500 opacity-20" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Failed Fixes</p>
                                <p className="text-3xl font-bold text-red-500">
                                    {history.filter(h => !h.success).length}
                                </p>
                            </div>
                            <XCircle className="h-10 w-10 text-red-500 opacity-20" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Executions</p>
                                <p className="text-3xl font-bold">{history.length}</p>
                            </div>
                            <History className="h-10 w-10 text-purple-500 opacity-20" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* AI Recommendations Panel */}
            {recommendations && (
                <Card className="border-2 border-purple-200 bg-purple-50/50 dark:bg-purple-950/20 dark:border-purple-800">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Zap className="h-5 w-5 text-purple-500" />
                            AI-Recommended Fixes
                        </CardTitle>
                        <CardDescription>
                            {recommendations.fix_order || 'Based on your current security posture'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {recommendations.recommended_fixes?.length > 0 ? (
                            <div className="space-y-3">
                                {recommendations.recommended_fixes.map((rec, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 font-bold text-sm">
                                                {rec.priority || idx + 1}
                                            </div>
                                            <div>
                                                <p className="font-medium">{rec.action?.replace(/_/g, ' ').toUpperCase()}</p>
                                                <p className="text-sm text-muted-foreground">{rec.reason}</p>
                                                <p className="text-xs text-muted-foreground">Target: {rec.target}</p>
                                            </div>
                                        </div>
                                        <Button 
                                            size="sm" 
                                            onClick={() => handleQuickFix(rec.action, rec.target)}
                                            disabled={executing}
                                            data-testid={`quick-fix-${idx}`}
                                        >
                                            <Play className="h-4 w-4 mr-1" />
                                            Apply
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-center py-4">No recommendations at this time</p>
                        )}
                        
                        {recommendations.warnings?.length > 0 && (
                            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <p className="font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4" />
                                    Warnings
                                </p>
                                <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                                    {recommendations.warnings.map((w, idx) => (
                                        <li key={idx}>{w}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="available" data-testid="tab-available">
                        <Wrench className="h-4 w-4 mr-2" />
                        Available Fixes ({availableFixes.length})
                    </TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">
                        <History className="h-4 w-4 mr-2" />
                        Execution History ({history.length})
                    </TabsTrigger>
                </TabsList>

                {/* Available Fixes Tab */}
                <TabsContent value="available" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {availableFixes.map((fix) => (
                            <Card key={fix.id} className="hover:shadow-md transition-shadow">
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-2">
                                            {getFixIcon(fix.id)}
                                            <CardTitle className="text-base">{fix.name}</CardTitle>
                                        </div>
                                        {getRiskBadge(fix.risk_level)}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-3">{fix.description}</p>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                                        <span>Applies to: {fix.applicable_to?.join(', ')}</span>
                                        {fix.requires_restart && (
                                            <Badge variant="outline" className="text-xs">Requires Restart</Badge>
                                        )}
                                    </div>
                                    <Button 
                                        className="w-full" 
                                        size="sm"
                                        onClick={() => {
                                            setSelectedFix(fix);
                                            setShowExecuteDialog(true);
                                        }}
                                        data-testid={`execute-${fix.id}-btn`}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Execute Fix
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history" className="space-y-4">
                    {history.length > 0 ? (
                        <div className="space-y-3">
                            {history.map((item, idx) => (
                                <Card key={idx}>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {getFixIcon(item.action)}
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium">{item.fix_name || item.action?.replace(/_/g, ' ')}</p>
                                                        {getStatusBadge(item.success)}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">Target: {item.target}</p>
                                                    <p className="text-sm text-muted-foreground">{item.message}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-muted-foreground">
                                                    {new Date(item.executed_at).toLocaleString()}
                                                </p>
                                                {item.changes_made?.length > 0 && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {item.changes_made.length} changes made
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {item.error && (
                                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-950/30 rounded text-sm text-red-600 dark:text-red-400">
                                                Error: {item.error}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <History className="h-16 w-16 text-muted-foreground mb-4" />
                                <h3 className="text-xl font-bold">No Execution History</h3>
                                <p className="text-muted-foreground">Execute your first auto-fix to see history here</p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>

            {/* Execute Fix Dialog */}
            <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedFix && getFixIcon(selectedFix.id)}
                            Execute: {selectedFix?.name}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedFix?.description}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Target</Label>
                            <Input 
                                placeholder={selectedFix ? getTargetPlaceholder(selectedFix.id) : 'Target'}
                                value={executeForm.target}
                                onChange={(e) => setExecuteForm({...executeForm, target: e.target.value})}
                                data-testid="fix-target-input"
                            />
                        </div>
                        
                        {selectedFix?.id === 'kill_process' && (
                            <div>
                                <Label>Process Name</Label>
                                <Input 
                                    placeholder="e.g., malware.exe"
                                    value={executeForm.parameters.process_name || ''}
                                    onChange={(e) => setExecuteForm({
                                        ...executeForm, 
                                        parameters: {...executeForm.parameters, process_name: e.target.value}
                                    })}
                                />
                            </div>
                        )}
                        
                        {selectedFix?.id === 'restart_service' && (
                            <div>
                                <Label>Service Name</Label>
                                <Input 
                                    placeholder="e.g., nginx"
                                    value={executeForm.parameters.service_name || ''}
                                    onChange={(e) => setExecuteForm({
                                        ...executeForm, 
                                        parameters: {...executeForm.parameters, service_name: e.target.value}
                                    })}
                                />
                            </div>
                        )}
                        
                        {selectedFix?.id === 'block_ip' && (
                            <div>
                                <Label>Block Duration (hours)</Label>
                                <Input 
                                    type="number"
                                    placeholder="24"
                                    value={executeForm.parameters.duration_hours || ''}
                                    onChange={(e) => setExecuteForm({
                                        ...executeForm, 
                                        parameters: {...executeForm.parameters, duration_hours: parseInt(e.target.value)}
                                    })}
                                />
                            </div>
                        )}

                        {selectedFix?.risk_level === 'high' && (
                            <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4" />
                                    High Risk Action: This action may cause service disruption
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExecuteDialog(false)}>Cancel</Button>
                        <Button 
                            onClick={handleExecuteFix} 
                            disabled={executing || !executeForm.target}
                            data-testid="confirm-execute-btn"
                        >
                            {executing ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Play className="h-4 w-4 mr-2" />
                            )}
                            Execute Fix
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Wrap with FeatureGate for Autofix tier requirement
const AutoFixPage = () => {
    return (
        <FeatureGate feature="autofix" featureName="Auto-Fix & Remediation">
            <AutoFixContent />
        </FeatureGate>
    );
};

export default AutoFixPage;
